import { get, set, remove, update } from './lib/gsru.js';
import { createMarker, createChildMarker, dispatch } from './lib/marker.js';
import { pack } from './admin/pack.js';
import { unpack } from './admin/unpack.js';
import { clear } from './lib/tree/mutate.js';
import { findFirstNode, findAllNodes } from './lib/tree/seek.js';
import { CREATED, DELETED, UPDATED } from './lib/TOKENS.js';
import { createChange } from './lib/change.js';
import { clone } from './utils/clone.js';

export class State {
  #state;
  #marker;
  #batchDepth = 0;
  #changeHandler;
  #accessHandler;
  #plugins = new Map();

  static create (state = {}) {
    const instance = new State(state);

    return instance;
  }

  constructor(state, marker = null, changeHandler = null, accessHandler = null) {
    this.#state = state;
    this.#changeHandler = changeHandler || this.#createChangeHandler();
    this.#accessHandler = accessHandler || this.#createAccessHandler();
    this.#marker = marker || createMarker();
    this.#batchDepth = 0;
  }

  install(...plugins) {
    const initializeFunctions = new Map();

    for (const plugin of plugins) {
      if (this.#plugins.has(plugin.name)) {
        console.warn(`Plugin "${plugin.name}" already installed`);
        continue;
      }

      if (!plugin.install) {
        console.warn(`Plugin "${plugin.name}" does not have an install function`);
        continue;
      }

      const pluginInstance = plugin.install(this);
      this.#plugins.set(plugin.name, pluginInstance);

      if (pluginInstance.initialize) {
        initializeFunctions.set(plugin.name, () => pluginInstance.initialize(this, pluginInstance.options));
      }

      if (pluginInstance.actions) {
        const actions = pluginInstance.actions(this, pluginInstance.options);
        Object.defineProperty(this, plugin.name, {
          value: actions,
          writable: false,
          enumerable: true,
          configurable: false
        });
      }
    }

    initializeFunctions.forEach(initialize => initialize());
    return this;
  }

  get batching() {
    return this.#batchDepth > 0;
  }

  // Core State Accessors
  get(...args) {
    return get(this.#state, createChildMarker(this.#marker, args), this.#accessHandler);
  }

  clone(...args) {
    const data = get(this.#state, createChildMarker(this.#marker, args), this.#accessHandler);
    if (typeof data === 'object' && data !== null) {
      try {
        return clone(data);
      } catch (_) {
        console.warn(`Failed to clone data: ${data}`);
        return data;
      }
    }
    return data;
  }

  has(...args) {
    const marker = createChildMarker(this.#marker, args);
    return dispatch(marker, {
      global: () => undefined,
      path: (marker) => get(this.#state, marker, this.#accessHandler) !== undefined,
    });
  }

  keys(...args) {
    const marker = createChildMarker(this.#marker, args);
    return dispatch(marker, {
      global: () => Object.keys(this.#state),
      path: (marker) => Object.keys(get(this.#state, marker, this.#accessHandler) || {}),
    });
  }

  typeof(...args) {
    const marker = createChildMarker(this.#marker, args);
    const value = dispatch(marker, {
      global: () => undefined,
      path: ({ path }) => typeof get(this.#state, path, this.#accessHandler),
    });
    return value;
  }

  // Queries
  findWhere(key, matcher = 'is', value) {
    const state = get(this.#state, this.#marker, null);
    return findFirstNode(state, key, matcher, value);
  }

  findAllWhere(key, matcher = 'is', value) {
    const state = get(this.#state, this.#marker, null);
    return findAllNodes(state, key, matcher, value);
  }

  // Mutations
  set(...args) {
    const value = args.pop();    
    const marker = createChildMarker(this.#marker, args);
    // No sync tombstone check - let async processing handle conflicts
    return set(this.#state, marker, value, 'set', this.#changeHandler);
  }

  update(...args) {
    const fn = args.pop();
    const marker = createChildMarker(this.#marker, args);
    
    // No sync tombstone check - let async processing handle conflicts  
    return update(this.#state, marker, fn, 'update', this.#changeHandler);
  }

  apply(changeEntries) {
    for (const changeEntry of changeEntries) {
      const { path, operation, to } = changeEntry;

      const marker = createChildMarker(this.#marker, path);
      switch (operation) {
        case CREATED:
        case UPDATED:
          set(this.#state, marker, to, 'apply', this.#changeHandler);
          break;
        case DELETED:
          remove(this.#state, marker, 'apply', this.#changeHandler);
          break;
      }
    }
  }

  load(value) {
    set(this.#state, this.#marker, value, 'load', this.#changeHandler);
    return this;
  }

  remove(...args) {
    const marker = createChildMarker(this.#marker, args);
    return remove(this.#state, marker, 'remove', this.#changeHandler);
  }

  // Lifecycle / Admin
  async export(...args) {
    try {
      const marker = createChildMarker(this.#marker, args);
      const promises = dispatch(marker, {
        global: async () => await pack(this.#state),
        path: async (path) => await pack(get(this.#state, path, this.#accessHandler)),
      });
      if (!Array.isArray(promises)) {
        return await promises;
      }
      return await Promise.all(promises);
    } catch (err) {
      console.trace(`Failed to export state: ${err.message}`);
      throw err
    }
  }

  async import(exportedData) {
    try {
      const { data } = await unpack(exportedData);
      this.load(data);
      return this;
    } catch (err) {
      console.trace(`Failed to import state: ${err.message}`);
      throw err
    }
  }

  reset() {
    clear(this.#state);
    for (const plugin of this.#plugins.values()) {
      if (plugin.reset) {
        plugin.reset();
      }
    }
    this.#changeHandler = this.#createChangeHandler();
    this.#marker = createMarker([], this);
    this.#batchDepth = 0;
  }


  // Reactivity Utilities
  ref(...path) {
    const marker = createChildMarker(this.#marker, path);
    return new State(this.#state, marker, this.#changeHandler, this.#accessHandler);
  }

  batch(fn) {
    this.#batchDepth++;
    this.#notifyPlugins('onBatchStart', this.batching);
    if (fn) {
      fn(this);
      return this.endBatch();
    }
    return () => this.endBatch();
  }

  endBatch() {
    this.#batchDepth--;
    if (this.#batchDepth === 0) {
      this.#notifyPlugins('onBatchEnd');
    }
    return this;
  }

  // Private change handler
  #createChangeHandler() {
    return (marker, method, to = undefined, from = undefined) => {
      this.#notifyPlugins('onStateChange', marker, createChange(marker, method, to, from), this.batching);
    }
  }

  #createAccessHandler() {
    return (marker) => this.#notifyPlugins('onStateAccess', marker);
  }

  #notifyPlugins(event, ...args) {
    for (const plugin of this.#plugins.values()) {
      if (typeof plugin[event] === 'function') {
        plugin[event](...args);
      }
    }
  }
}

export const createState = (...args) => State.create(...args);
export const isState = (target) => target instanceof State;